import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Paperclip, Upload, Download, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatSize = (bytes) => {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

export const RTBFAttachmentsDialog = ({ open, onOpenChange, request, canDelete }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const fileInputRef = useRef(null);

  const rtbfId = request?.rtbf_id;

  const load = async () => {
    if (!rtbfId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/rtbf/${rtbfId}/attachments`,
        { withCredentials: true }
      );
      setAttachments(data.attachments || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar anexos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { setDescription(''); load(); } }, [open, rtbfId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Ficheiro excede 15 MB');
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('description', description);
    try {
      await axios.post(
        `${BACKEND_URL}/api/rtbf/${rtbfId}/attachments`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Anexo carregado');
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/rtbf/${rtbfId}/attachments/${att.attachment_id}/download`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename || att.attachment_id;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao descarregar');
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Remover o anexo "${att.filename}"?`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/rtbf/${rtbfId}/attachments/${att.attachment_id}`,
        { withCredentials: true }
      );
      toast.success('Anexo removido');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover anexo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="rtbf-attachments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-5 h-5" /> Anexos de evidência
          </DialogTitle>
          <DialogDescription>
            Junte screenshots, exports e comprovativos. Ficam associados ao pedido e são contabilizados
            na Prova de Cumprimento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Descrição (opcional)</Label>
              <Input
                placeholder="ex.: screenshot do CRM antes da anonimização"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="attachment-description-input"
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleUpload}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx"
              data-testid="attachment-file-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="attachment-upload-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'A carregar...' : 'Adicionar ficheiro'}
            </Button>
          </div>

          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
            {loading ? (
              <p className="p-6 text-center text-slate-500 text-sm">A carregar...</p>
            ) : attachments.length === 0 ? (
              <p className="p-6 text-center text-slate-500 text-sm">Sem anexos associados a este pedido.</p>
            ) : (
              attachments.map((att) => (
                <div
                  key={att.attachment_id}
                  className="p-3 flex items-center gap-3"
                  data-testid={`attachment-row-${att.attachment_id}`}
                >
                  <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{att.filename}</p>
                    <p className="text-xs text-slate-500">
                      {formatSize(att.size_bytes)} · {att.content_type}
                      {att.description ? ` · ${att.description}` : ''}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      por {att.uploaded_by_email} · {new Date(att.uploaded_at).toLocaleString('pt-PT')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(att)}
                    data-testid={`attachment-download-${att.attachment_id}`}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(att)}
                      data-testid={`attachment-delete-${att.attachment_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RTBFAttachmentsDialog;
